import type { ScheduleDto, TimeRangeDto, TimeOffDto } from '@/services/api/petseo.client';
import { formatDateToString } from '@petseo/shared';
import { getDayOfWeek } from './dayOfWeek';

export const getStartOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

export const getEndOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

export const getWeekStart = (date: Date): Date => {
  const result = new Date(date);
  const dayOfWeek = result.getDay();
  const difference = result.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  result.setDate(difference);
  return getStartOfDay(result);
};

export const getWeekEnd = (date: Date): Date => {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return getEndOfDay(end);
};

export const getWeekDays = (date: Date): Date[] => {
  const start = getWeekStart(date);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    return day;
  });
};

export const addWeeks = (date: Date, weeks: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + weeks * 7);
  return result;
};

export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
};

export const formatDateLong = (date: Date | string | undefined, fallback = '-'): string => {
  if (!date) return fallback;
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

export const formatDateShort = (date: Date | string | undefined, fallback = '-'): string => {
  if (!date) return fallback;
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatDateRange = (start: Date, end: Date): string => {
  const startMonth = start.toLocaleDateString('pl-PL', { month: 'long' });
  const endMonth = end.toLocaleDateString('pl-PL', { month: 'long' });
  const year = start.getFullYear();

  if (startMonth === endMonth) {
    return `${start.getDate()}-${end.getDate()} ${startMonth} ${year}`;
  }
  return `${start.getDate()} ${startMonth} - ${end.getDate()} ${endMonth} ${year}`;
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
};

export const formatDatePolish = (date: Date | string | undefined): string => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

export const formatDateTimePolish = (date?: Date | string): string => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export { formatDateToString };

export const parseDateFromApi = (dateString: string | Date | undefined): Date | null => {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;
  return new Date(dateString);
};

export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toDateInputValue = (value?: string | Date): string => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatDateForInput(date);
};

export const getInclusiveDayCount = (dateFrom: string, dateTo: string): number | null => {
  if (!dateFrom || !dateTo) return null;
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) return null;
  const diffMs = to.getTime() - from.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
};

export { toApiDateFormat } from '@petseo/shared';

export const getMonthStart = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

export const getMonthEnd = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
};

export const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

export const getMonthWeeks = (date: Date): Date[][] => {
  const start = getMonthStart(date);
  const end = getMonthEnd(date);
  const weeks: Date[][] = [];
  const firstDay = start.getDay();
  const startOffset = firstDay === 0 ? -6 : 1 - firstDay;
  const current = new Date(start);
  current.setDate(current.getDate() + startOffset);

  while (current <= end || current.getDay() !== 1) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);
    if (current > end && current.getDay() === 1) break;
  }

  return weeks;
};

export const formatMonthYear = (date: Date): string => {
  return date.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
};

export const toDateOnly = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const isScheduleActiveForDay = (
  schedule: { validFrom?: Date; validTo?: Date; days?: { dayOfWeek?: number }[] },
  dateString: string,
  dayOfWeek: number
): boolean => {
  if (!schedule.validFrom || !schedule.validTo) return false;
  const from = toDateOnly(new Date(schedule.validFrom));
  const to = toDateOnly(new Date(schedule.validTo));
  if (dateString < from || dateString > to) return false;
  return (schedule.days ?? []).some((d) => d.dayOfWeek === dayOfWeek);
};

export const getDayScheduleBlocks = (day: Date, schedules: ScheduleDto[]): { schedule: ScheduleDto; ranges: TimeRangeDto[] }[] => {
  const dayOfWeek = getDayOfWeek(day);
  const dateString = formatDateToString(day);

  return schedules
    .filter((s) => isScheduleActiveForDay(s, dateString, dayOfWeek))
    .map((schedule) => {
      const dayEntry = (schedule.days ?? []).find((d) => d.dayOfWeek === dayOfWeek);
      const ranges = (dayEntry?.timeRanges ?? []).slice().sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''));
      return { schedule, ranges };
    })
    .filter((entry) => entry.ranges.length > 0);
};

export const buildTimeOffByDateMap = (timeOffs: TimeOffDto[] | undefined): Map<string, { reason?: string | null }> => {
  const map = new Map<string, { reason?: string | null }>();
  for (const t of timeOffs ?? []) {
    if (!t.dateFrom || !t.dateTo) continue;
    const from = new Date(t.dateFrom);
    const to = new Date(t.dateTo);
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      map.set(formatDateToString(new Date(d)), { reason: t.reason });
    }
  }
  return map;
};

export const getNowIso = (): string => new Date().toISOString();

export const getDefaultDateRange = (daysAhead: number = 7): { from: string; to: string } => {
  const today = new Date();
  const future = new Date(today);
  future.setDate(future.getDate() + daysAhead);
  return {
    from: formatDateForInput(today),
    to: formatDateForInput(future),
  };
};
